// UnifiedMobileSystem.js
// Enterprise-grade unified mobile system - Supreme quality implementation
// Implements enterprise patterns: Singleton, Factory, Observer, Strategy

import { MOBILE_CONSTANTS, BREAKPOINTS, TOUCH_TARGETS, Z_INDEX, PERFORMANCE, TIMING, THEME } from '../config/MobileConstants.js';
import { IMobileInputHandler, IErrorBoundary } from '../interfaces/IMobileSystem.js';

/**
 * Enterprise error boundary implementation
 */
class EnterpriseErrorBoundary extends IErrorBoundary {
    static wrap(fn, fallback, context = {}) {
        try {
            return fn();
        } catch (error) {
            console.error('🚨 Enterprise Error Boundary:', error.message, context);
            
            // Report to analytics if available
            if (window.mobileAnalytics?.trackError) {
                window.mobileAnalytics.trackError(error, context);
            }
            
            return fallback;
        }
    }
    
    static async wrapAsync(fn, fallback, context = {}) {
        try {
            return await fn();
        } catch (error) {
            console.error('🚨 Enterprise Async Error Boundary:', error.message, context);
            
            // Report to analytics if available
            if (window.mobileAnalytics?.trackError) {
                window.mobileAnalytics.trackError(error, context);
            }
            
            return fallback;
        }
    }
}

/**
 * Enterprise-grade unified mobile system
 * Implements multiple design patterns for supreme quality
 */
export class UnifiedMobileSystem extends IMobileInputHandler {
    static _instance = null;
    
    /**
     * Singleton pattern implementation
     */
    static getInstance() {
        if (!UnifiedMobileSystem._instance) {
            UnifiedMobileSystem._instance = new UnifiedMobileSystem();
        }
        return UnifiedMobileSystem._instance;
    }
    
    constructor() {
        super();
        if (UnifiedMobileSystem._instance) {
            return UnifiedMobileSystem._instance;
        }
        
        this.isInitialized = false;
        this.deviceInfo = null;
        this.currentScene = null;
        this.eventDelegation = new Map();
        this.observers = new Set();
        // Error boundary is static - no instance needed
        
        // Single source of truth for mobile detection
        this.detectDevice();
        
        UnifiedMobileSystem._instance = this;
    }
    
    /**
     * Comprehensive device detection - Single source of truth
     */
    detectDevice() {
        const userAgent = navigator.userAgent;
        const width = window.innerWidth;
        const height = window.innerHeight;
        
        // iPhone specific detection
        const isIPhone = /iPhone/i.test(userAgent);
        const isIPad = /iPad/i.test(userAgent);
        const isAndroid = /Android/i.test(userAgent);
        const isMobileUA = /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent);
        const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        
        // Screen size analysis using enterprise constants
        const isMobileSize = width <= BREAKPOINTS.MOBILE;
        const isTabletSize = width > BREAKPOINTS.MOBILE && width <= BREAKPOINTS.TABLET;
        const isDesktopSize = width > BREAKPOINTS.DESKTOP;
        
        // Determine device type with iPhone-specific handling
        let deviceType = 'desktop';
        if (isIPhone || (isMobileUA && width <= BREAKPOINTS.MOBILE_SMALL)) {
            deviceType = 'mobile-phone';
        } else if (isIPad || (isMobileUA && width <= BREAKPOINTS.MOBILE)) {
            deviceType = 'mobile-tablet';
        } else if (isMobileSize || isMobileUA) {
            deviceType = 'mobile';
        } else if (isTabletSize) {
            deviceType = 'tablet';
        }
        
        this.deviceInfo = {
            type: deviceType,
            isMobile: deviceType.includes('mobile'),
            isIPhone,
            isIPad,
            isAndroid,
            width,
            height,
            pixelRatio: window.devicePixelRatio,
            touchSupport: hasTouchSupport,
            orientation: width > height ? 'landscape' : 'portrait'
        };
        
        console.log('📱 Device detected:', this.deviceInfo);
        return this.deviceInfo;
    }
    
    /**
     * Initialize the unified mobile system
     */
    initialize() {
        return EnterpriseErrorBoundary.wrap(
            () => {
                if (this.isInitialized) return;
                
                console.log('🎯 Initializing UnifiedMobileSystem...');
                
                if (!this.deviceInfo.isMobile) {
                    console.log('💻 Desktop device - mobile system not needed');
                    return;
                }
                
                // Enterprise initialization with explicit method binding (prevents minification issues)
                try {
                    this.fixViewportAndCSS();
                    console.log('✅ fixViewportAndCSS completed');
                } catch (error) {
                    console.error('❌ fixViewportAndCSS failed:', error);
                }
                
                try {
                    this.setupUnifiedEventHandling();
                    console.log('✅ setupUnifiedEventHandling completed');
                } catch (error) {
                    console.error('❌ setupUnifiedEventHandling failed:', error);
                }
                
                try {
                    this.fixSceneTransitions();
                    console.log('✅ fixSceneTransitions completed');
                } catch (error) {
                    console.error('❌ fixSceneTransitions failed:', error);
                }
                
                this.isInitialized = true;
                console.log('✅ UnifiedMobileSystem initialized with enterprise error handling');
                
                // Notify observers using observer pattern
                this.notifyObservers('system.initialized', { deviceInfo: this.deviceInfo });
                
                // NO global window pollution - use dependency injection instead
                this.registerWithServiceLocator();
            },
            () => {
                console.error('❌ Critical failure initializing UnifiedMobileSystem');
                this.initializeFallbackMode();
            },
            { service: 'UnifiedMobileSystem', method: 'initialize' }
        );
    }
    
    /**
     * Register with service locator pattern (no global pollution)
     */
    registerWithServiceLocator() {
        if (!window.serviceLocator) {
            window.serviceLocator = new Map();
        }
        window.serviceLocator.set('UnifiedMobileSystem', this);
        console.log('✅ UnifiedMobileSystem registered with service locator');
    }
    
    /**
     * Observer pattern implementation
     */
    addObserver(observer) {
        this.observers.add(observer);
    }
    
    removeObserver(observer) {
        this.observers.delete(observer);
    }
    
    notifyObservers(event, data) {
        this.observers.forEach(observer => {
            EnterpriseErrorBoundary.wrap(
                () => observer(event, data),
                null,
                { service: 'UnifiedMobileSystem', method: 'notifyObservers', event }
            );
        });
    }
    
    /**
     * Fallback mode for critical failures
     */
    initializeFallbackMode() {
        console.warn('🚨 Initializing UnifiedMobileSystem fallback mode');
        
        // Minimal CSS fixes only
        if (this.deviceInfo?.isMobile) {
            document.body.style.overflow = 'auto';
            document.body.style.webkitOverflowScrolling = 'touch';
            
            const container = document.getElementById('game-container');
            if (container) {
                container.style.overflow = 'auto';
                container.style.webkitOverflowScrolling = 'touch';
            }
        }
        
        this.isInitialized = true; // Mark as initialized even in fallback mode
    }
    
    /**
     * Fix viewport and CSS conflicts - Single authoritative solution
     */
    fixViewportAndCSS() {
        console.log('📐 Fixing viewport and CSS conflicts...');
        
        // Remove ALL conflicting CSS
        const conflictingStyles = document.querySelectorAll('#guest-mobile-enterprise-css, #mobile-responsive-css, #native-mobile-scrolling, #unified-mobile-system');
        conflictingStyles.forEach(style => style.remove());
        
        // CRITICAL FIX: Force enable scrolling directly on elements (bypasses CSS conflicts)
        this.forceEnableScrollingOnElements();
        
        console.log('✅ Mobile CSS conflicts resolved and scrolling force-enabled');
    }
    
    /**
     * Force enable scrolling directly on DOM elements (bypasses all CSS conflicts)
     */
    forceEnableScrollingOnElements() {
        console.log('🔧 Force enabling scrolling on iPhone...');
        
        // Get all potentially scrollable elements
        const elements = [
            document.documentElement,
            document.body,
            document.getElementById('game-container'),
            document.querySelector('canvas'),
            ...document.querySelectorAll('[id$="-overlay"], .overlay')
        ].filter(Boolean);
        
        elements.forEach((element, index) => {
            if (element.tagName === 'CANVAS') {
                // Canvas specific fixes for iPhone
                element.style.setProperty('touch-action', 'pan-y', 'important');
                element.style.setProperty('pointer-events', 'auto', 'important');
                element.style.setProperty('position', 'static', 'important');
            } else {
                // General scrollable element fixes
                element.style.setProperty('overflow', 'auto', 'important');
                element.style.setProperty('overflow-y', 'auto', 'important');
                element.style.setProperty('-webkit-overflow-scrolling', 'touch', 'important');
                element.style.setProperty('overscroll-behavior', 'contain', 'important');
                element.style.setProperty('touch-action', 'pan-y', 'important');
                
                if (element === document.body || element === document.documentElement) {
                    element.style.setProperty('height', 'auto', 'important');
                    element.style.setProperty('position', 'static', 'important');
                }
                
                if (element.id === 'game-container') {
                    element.style.setProperty('position', 'fixed', 'important');
                    element.style.setProperty('top', '0', 'important');
                    element.style.setProperty('left', '0', 'important');
                    element.style.setProperty('width', '100vw', 'important');
                    element.style.setProperty('height', '100vh', 'important');
                    element.style.setProperty('z-index', '1', 'important');
                }
            }
            
            console.log(`✅ Element ${index + 1} (${element.tagName}) scrolling enabled`);
        });
        
        // Additional iPhone Safari specific fixes
        if (/iPhone|iPad/.test(navigator.userAgent)) {
            console.log('🍎 Applying iPhone Safari specific fixes...');
            
            // Fix iOS Safari viewport issues
            document.documentElement.style.setProperty('height', '100%', 'important');
            document.body.style.setProperty('height', '100%', 'important');
            
            // Fix iOS Safari scroll momentum
            document.body.style.setProperty('-webkit-overflow-scrolling', 'touch', 'important');
            
            // Disable iOS Safari bounce (but keep scrolling)
            document.body.style.setProperty('overscroll-behavior-y', 'contain', 'important');
            
            console.log('✅ iPhone Safari specific fixes applied');
        }
    }
    
    /**
     * Set up unified event handling - MOBILE ONLY (enterprise separation of concerns)
     */
    setupUnifiedEventHandling() {
        console.log('🎯 Setting up unified event handling for MOBILE...');
        
        // ENTERPRISE: Strict mobile-only enforcement
        if (!this.deviceInfo.isMobile) {
            console.log('💻 Desktop detected - UnifiedMobileSystem will NOT interfere with desktop events');
            return;
        }
        
        // CRITICAL: Fix Phaser DOM container conflicts (mobile only)
        this.fixPhaserDOMConflicts();
        
        // Mobile-only event delegation
        document.addEventListener('click', (e) => {
            // Double-check mobile context before handling
            if (this.deviceInfo.isMobile) {
                this.handleUnifiedClick(e);
            }
        }, { passive: false });
        
        // Mobile-only gesture detection
        this.setupNativeGestures();
        
        console.log('✅ Unified event handling active (MOBILE ONLY)');
    }
    
    /**
     * Fix Phaser DOM container conflicts that prevent scrolling
     */
    fixPhaserDOMConflicts() {
        console.log('🔧 Fixing Phaser DOM container conflicts...');
        
        // Find all Phaser-managed DOM overlays
        const phaserOverlays = document.querySelectorAll('#game-container [id$="-overlay"]');
        
        phaserOverlays.forEach((overlay, index) => {
            console.log(`🔧 Fixing Phaser overlay ${index + 1}: ${overlay.id}`);
            
            // CRITICAL: Move overlay OUT of Phaser's game-container to document.body
            if (overlay.parentElement && overlay.parentElement.id === 'game-container') {
                console.log(`📤 Moving ${overlay.id} from game-container to body`);
                
                // Clone the overlay
                const newOverlay = overlay.cloneNode(true);
                
                // Ensure it has proper mobile scrolling styles
                newOverlay.style.setProperty('position', 'fixed', 'important');
                newOverlay.style.setProperty('top', '0', 'important');
                newOverlay.style.setProperty('left', '0', 'important');
                newOverlay.style.setProperty('width', '100vw', 'important');
                newOverlay.style.setProperty('height', '100vh', 'important');
                newOverlay.style.setProperty('overflow-y', 'auto', 'important');
                newOverlay.style.setProperty('-webkit-overflow-scrolling', 'touch', 'important');
                newOverlay.style.setProperty('overscroll-behavior', 'contain', 'important');
                newOverlay.style.setProperty('touch-action', 'pan-y', 'important');
                newOverlay.style.setProperty('z-index', '15000', 'important'); // Higher than Phaser
                
                // Replace the old overlay
                document.body.appendChild(newOverlay);
                overlay.remove();
                
                console.log(`✅ ${overlay.id} moved to body with mobile scrolling enabled`);
            }
        });
        
        // Also fix any future overlays created by Phaser scenes
        this.observePhaserOverlayCreation();
        
        console.log('✅ Phaser DOM conflicts fixed');
    }
    
    /**
     * Observe and fix any new overlays created by Phaser scenes
     */
    observePhaserOverlayCreation() {
        // Use MutationObserver to catch new overlays
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE && 
                        node.id && node.id.endsWith('-overlay') &&
                        node.parentElement && node.parentElement.id === 'game-container') {
                        
                        console.log(`🔧 Auto-fixing new Phaser overlay: ${node.id}`);
                        
                        // Apply mobile scrolling fixes immediately
                        node.style.setProperty('overflow-y', 'auto', 'important');
                        node.style.setProperty('-webkit-overflow-scrolling', 'touch', 'important');
                        node.style.setProperty('touch-action', 'pan-y', 'important');
                        node.style.setProperty('z-index', '15000', 'important');
                        
                        // Move to body if needed
                        if (this.deviceInfo.isMobile) {
                            document.body.appendChild(node);
                            console.log(`✅ Auto-moved ${node.id} to body for mobile scrolling`);
                        }
                    }
                });
            });
        });
        
        // Observe the game container for new overlays
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            observer.observe(gameContainer, { childList: true, subtree: true });
            console.log('👁️ Observing Phaser overlay creation for auto-fixes');
        }
    }
    
    /**
     * Handle all clicks through single delegation system
     */
    handleUnifiedClick(event) {
        const target = event.target;
        const button = target.closest('button, .btn, [role="button"], [data-action], [data-game]');
        
        if (!button) return;
        
        console.log('🔘 Unified click:', button.dataset.action || button.dataset.game || button.textContent);
        
        // Visual feedback
        button.style.transform = 'scale(0.95)';
        setTimeout(() => {
            button.style.transform = '';
        }, 150);
        
        // Handle action
        const action = button.dataset.action;
        const game = button.dataset.game;
        
        if (action) {
            this.handleAction(action);
        } else if (game) {
            this.handleGameAction(game);
        }
    }
    
    /**
     * Handle actions through current scene
     */
    handleAction(action) {
        const currentScene = this.getCurrentScene();
        if (currentScene && currentScene.handleAction) {
            currentScene.handleAction(action);
        } else {
            console.warn('❌ No scene to handle action:', action);
        }
    }
    
    /**
     * Handle game actions through current scene
     */
    handleGameAction(game) {
        const currentScene = this.getCurrentScene();
        if (currentScene && currentScene.handleGameAction) {
            currentScene.handleGameAction(game);
        } else {
            console.warn('❌ No scene to handle game action:', game);
        }
    }
    
    /**
     * Get current active scene
     */
    getCurrentScene() {
        return window.game?.scene?.scenes?.find(s => s.scene.isActive());
    }
    
    /**
     * Set up native gesture detection using PointerEvent API
     */
    setupNativeGestures() {
        let startX, startY, startTime;
        
        document.addEventListener('pointerdown', (e) => {
            if (e.pointerType === 'touch') {
                startX = e.clientX;
                startY = e.clientY;
                startTime = Date.now();
            }
        }, { passive: true });
        
        document.addEventListener('pointerup', (e) => {
            if (e.pointerType === 'touch' && startX !== undefined) {
                const deltaX = e.clientX - startX;
                const deltaY = e.clientY - startY;
                const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
                const time = Date.now() - startTime;
                
                if (distance > PERFORMANCE.GESTURE_THRESHOLD && time < 1000) {
                    if (Math.abs(deltaX) > Math.abs(deltaY)) {
                        if (deltaX > 0) {
                            console.log('👉 UNIFIED SWIPE RIGHT');
                            this.triggerSwipe('right');
                        } else {
                            console.log('👈 UNIFIED SWIPE LEFT');
                            this.triggerSwipe('left');
                        }
                    }
                }
                
                startX = startY = undefined;
            }
        }, { passive: true });
    }
    
    /**
     * Trigger swipe actions with enterprise mobile navigation patterns
     */
    triggerSwipe(direction) {
        console.log(`📱 Swipe ${direction} detected - executing mobile navigation`);
        
        if (direction === 'left') {
            // Go to main menu or back
            const mainMenuBtn = document.getElementById('ribbon-mainmenu-btn');
            if (mainMenuBtn) {
                this.showSwipeFeedback('← Main Menu');
                mainMenuBtn.click();
            } else {
                // Alternative: Browser back navigation
                this.showSwipeFeedback('← Back');
                history.back();
            }
        } else if (direction === 'right') {
            // Context-dependent right swipe actions
            const currentScene = this.getCurrentScene()?.scene?.key;
            
            if (currentScene === 'GuestMenuSceneDOM') {
                this.showSwipeFeedback('Sign In →');
                const signInBtn = document.querySelector('[data-action="sign-in"]');
                if (signInBtn) signInBtn.click();
            } else if (currentScene === 'AuthenticatedMenuSceneDOM') {
                this.showSwipeFeedback('Analytics →');
                const analyticsBtn = document.getElementById('ribbon-analytics-btn');
                if (analyticsBtn) analyticsBtn.click();
            }
        } else if (direction === 'up') {
            // Scroll to top
            this.showSwipeFeedback('↑ Top');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        
        // Track swipe analytics
        if (window.mobileAnalytics?.trackEvent) {
            window.mobileAnalytics.trackEvent('mobile_swipe_navigation', {
                direction,
                scene: this.getCurrentScene()?.scene?.key
            });
        }
    }
    
    /**
     * Show visual feedback for swipe actions
     */
    showSwipeFeedback(action) {
        const feedback = document.createElement('div');
        feedback.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 212, 255, 0.9);
            color: white;
            padding: 12px 20px;
            border-radius: 20px;
            font-size: 14px;
            font-weight: 600;
            z-index: 50000;
            pointer-events: none;
            animation: swipeFeedback 1s ease-out;
        `;
        
        feedback.textContent = action;
        document.body.appendChild(feedback);
        
        // Remove after animation
        setTimeout(() => {
            if (feedback.parentElement) {
                feedback.remove();
            }
        }, 1000);
    }
    
    /**
     * Fix scene transition system to prevent white flashes
     */
    fixSceneTransitions() {
        console.log('🎬 Fixing scene transitions...');
        
        // Override scene manager to prevent white flashes
        if (window.sceneManager && window.sceneManager.switchToScene) {
            const originalSwitch = window.sceneManager.switchToScene;
            
            window.sceneManager.switchToScene = function(sceneKey, data) {
                console.log('🎬 Unified scene transition:', sceneKey);
                
                // Ensure no white flash
                document.body.style.background = '#0f172a';
                document.getElementById('game-container').style.background = '#0f172a';
                
                return originalSwitch.call(this, sceneKey, data);
            };
        }
        
        console.log('✅ Scene transitions fixed');
    }
    
    /**
     * Get system status
     */
    getStatus() {
        return {
            isInitialized: this.isInitialized,
            deviceInfo: this.deviceInfo,
            currentScene: this.getCurrentScene()?.scene?.key,
            eventDelegationActive: this.eventDelegation.size > 0
        };
    }
}

// Export singleton class (not instance)
export default UnifiedMobileSystem;
